import "./HotTakePreviewResponses.scss";

import { Divider, Flex, Text } from "@fluentui/react-northstar";
import { NominalQuestion, OrdinalQuestion, TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { PollyIcon, PollyIconName } from "../../../../../_shared/components/PollyIcon";
import { HotTakeVoteType } from "./HotTakeVoteButton";

type HotTakePreviewResponsesProps = {
  survey: SurveyDTO;
  responses: TeamsSurveyResponse[];
};

export const HotTakePreviewResponses: React.FC<HotTakePreviewResponsesProps> = ({
  survey,
  responses,
}) => {
  const { user } = useUserInfo();

  const totalVotes = responses.length;

  const hotTakeQuestion = survey.questions[0] as OrdinalQuestion | NominalQuestion;
  const upVoteChoiceId = hotTakeQuestion.choices[0].id;
  const downVoteChoiceId = hotTakeQuestion.choices[1].id;

  const upVotes = responses.filter(
    response => response.responses[0].value === upVoteChoiceId
  ).length;
  const downVotes = responses.filter(
    response => response.responses[0].value === downVoteChoiceId
  ).length;

  const choices = hotTakeQuestion.choices;
  let userSelected: HotTakeVoteType | undefined;
  const userResponse = responses.find(r => r.user.aadObjectId === user.userId);
  if (userResponse) {
    userSelected = userResponse.responses[0].value === choices[0].id ? "up" : "down";
  }

  const upVotePercent = totalVotes > 0 ? upVotes / totalVotes : 0;
  const downVotePercent = totalVotes > 0 ? downVotes / totalVotes : 0;

  return (
    <div className="hot-take-preview">
      <HotTakeVotePreview vote="up" percent={upVotePercent} quantity={upVotes} />
      <HotTakeVotePreview vote="down" percent={downVotePercent} quantity={downVotes} />
      <Divider />
      <Text content={`Total votes: ${totalVotes}`} weight="semibold" />
      {userSelected && (
        <Flex gap="gap.smaller" vAlign="center">
          <Text content="You voted:" />
          <span className="polly-accent-color">
            <PollyIcon
              icon={userSelected === "up" ? "thumbs-up-icon" : "thumbs-down-icon"}
              inheritColor
            />
          </span>
        </Flex>
      )}
    </div>
  );
};

const HotTakeVotePreview: React.FC<{
  vote: HotTakeVoteType;
  percent: number;
  quantity: number;
}> = ({ vote, percent, quantity }) => {
  let baseClass: string = "hot-take-preview__row";
  let icon: PollyIconName;

  const valueClasses = [`${baseClass}__bar__value`];

  switch (vote) {
    case "up":
      valueClasses.push(`${baseClass}__bar__value--up-vote`);
      icon = "thumbs-up-icon";
      break;

    case "down":
      valueClasses.push(`${baseClass}__bar__value--down-vote`);
      icon = "thumbs-down-icon";
      break;
  }

  const formattedPercent = `${Math.floor(percent * 1000) / 10}%`;

  return (
    <div className={baseClass}>
      <PollyIcon icon={icon} />
      <div className={`${baseClass}__bar`}>
        <div className={`${baseClass}__bar__bg`}></div>
        <div className={valueClasses.join(" ")} style={{ width: formattedPercent }}></div>
      </div>
      <div className={`${baseClass}__quantity`}>
        <Text content={`${formattedPercent} (${quantity})`} />
      </div>
    </div>
  );
};
